import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { articlesData, articleCategories } from '@/mocks/articlesData';
import { getWebSiteSchema, getWebPageSchema, getBreadcrumbSchema, getArticleSchema } from '@/utils/schemas';

const articleImages: Record<string, string> = {
  'article-1': 'https://readdy.ai/api/search-image?query=Fresh%20green%20pears%20in%20a%20wooden%20basket%20on%20a%20rustic%20wooden%20table%2C%20soft%20natural%20window%20light%2C%20warm%20cozy%20farmhouse%20atmosphere%2C%20summer%20harvest%20aesthetic%2C%20editorial%20food%20photography%2C%20earthy%20green%20tones%2C%20organic%20composition&width=1200&height=600&seq=article-cuiguan-2026&orientation=landscape',
  'article-2': 'https://readdy.ai/api/search-image?query=A%20golden%20trophy%20and%20certificate%20placed%20next%20to%20beautiful%20golden%20pears%20on%20a%20elegant%20marble%20surface%2C%20soft%20natural%20lighting%2C%20prestigious%20award%20ceremony%20aesthetic%2C%20warm%20golden%20tones%2C%20editorial%20photography&width=1200&height=600&seq=article-award-2026&orientation=landscape',
  'article-3': 'https://readdy.ai/api/search-image?query=Cut%20pear%20slices%20arranged%20artistically%20on%20a%20white%20plate%20with%20nutritional%20herbs%20and%20honey%2C%20soft%20natural%20lighting%2C%20healthy%20food%20aesthetic%2C%20clean%20minimal%20composition%2C%20editorial%20food%20photography&width=1200&height=600&seq=article-nutrition&orientation=landscape',
  'article-4': 'https://readdy.ai/api/search-image?query=Steamed%20pear%20dessert%20in%20a%20beautiful%20ceramic%20bowl%20with%20rock%20sugar%20and%20goji%20berries%2C%20warm%20cozy%20lighting%2C%20traditional%20Chinese%20dessert%20aesthetic%2C%20steam%20rising%2C%20food%20photography&width=1200&height=600&seq=article-recipe&orientation=landscape',
  'article-5': 'https://readdy.ai/api/search-image?query=Autumn%20scene%20with%20ripe%20pears%20on%20a%20wooden%20table%20surrounded%20by%20fall%20leaves%2C%20warm%20golden%20hour%20light%2C%20cozy%20seasonal%20aesthetic%2C%20harvest%20theme%2C%20editorial%20photography%2C%20rustic%20charm&width=1200&height=600&seq=article-autumn-health&orientation=landscape',
  'article-6': 'https://readdy.ai/api/search-image?query=Modern%20smart%20orchard%20with%20drone%20flying%20over%20pear%20trees%2C%20digital%20technology%20in%20agriculture%2C%20warm%20sunset%20light%2C%20innovative%20farming%20aesthetic%2C%20professional%20photography&width=1200&height=600&seq=article-tech-2026&orientation=landscape',
  'article-7': 'https://readdy.ai/api/search-image?query=Close%20up%20of%20a%20hand%20selecting%20a%20perfect%20ripe%20pear%20from%20a%20wooden%20crate%20at%20a%20farmers%20market%2C%20natural%20outdoor%20lighting%2C%20fresh%20produce%20aesthetic%2C%20editorial%20photography&width=1200&height=600&seq=article-pick-pear&orientation=landscape',
  'article-8': 'https://readdy.ai/api/search-image?query=Elegant%20pear%20salad%20and%20baked%20pear%20dessert%20on%20a%20beautiful%20wooden%20table%2C%20soft%20natural%20lighting%2C%20gourmet%20food%20photography%2C%20artistic%20plating%2C%20warm%20inviting%20aesthetic&width=1200&height=600&seq=article-creative-recipe&orientation=landscape',
};

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articlesData.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <SEOHead
          title="文章未找到 - 惠美梨"
          description="抱歉，您查找的文章不存在。请浏览惠美梨其他资讯内容。"
          canonicalUrl="https://hwsxsp.cn/articles"
          schema={[getWebSiteSchema()]}
        />
        <Navbar />
        <main className="pt-16 md:pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您查找的文章不存在。</p>
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryName = articleCategories.find(c => c.id === article.category)?.name || article.category;
  const relatedArticles = articlesData
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const pageTitle = `${article.title} - 惠美梨梨果资讯`;
  const pageDesc = article.excerpt;
  const articleUrl = `https://hwsxsp.cn/articles/${article.id}`;

  const schemas = [
    getWebSiteSchema(),
    getWebPageSchema(article.title, pageDesc, articleUrl),
    getBreadcrumbSchema([
      { name: '首页', url: 'https://hwsxsp.cn/' },
      { name: '梨果资讯', url: 'https://hwsxsp.cn/articles' },
      { name: article.title, url: articleUrl },
    ]),
    getArticleSchema({
      title: article.title,
      description: article.excerpt,
      url: articleUrl,
      datePublished: article.date,
      dateModified: article.date,
      author: article.author,
      imageUrl: articleImages[article.id] || articleImages['article-1'],
    }),
  ];

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        canonicalUrl={articleUrl}
        ogType="article"
        schema={schemas}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">
        <article className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to="/articles" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">梨果资讯</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 line-clamp-1">{article.title}</span>
            </nav>

            <div className="mb-4">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                {categoryName}
              </span>
            </div>

            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-400 mb-8">
              <span>{article.date}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.author}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>阅读时长 {article.readTime}</span>
            </div>

            <div className="relative h-64 md:h-80 rounded-2xl overflow-hidden mb-10">
              <img
                src={articleImages[article.id] || articleImages['article-1']}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-base md:prose-lg max-w-none text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {relatedArticles.length > 0 && (
              <div className="mt-16 pt-10 border-t border-background-200/70">
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">相关文章</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/articles/${ra.id}`}
                      className="group rounded-xl bg-background-100 overflow-hidden cursor-pointer"
                    >
                      <div className="relative h-36 overflow-hidden">
                        <img
                          src={articleImages[ra.id] || articleImages['article-1']}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-400 mt-2">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10 text-center">
              <Link
                to="/articles"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-primary-500 text-primary-600 font-medium text-sm hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回文章列表
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}